import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Upload } from 'lucide-react';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface UploadedLogo {
  url: string;
  width: number;
  height: number;
  bytes: number;
  notes: string[];
}

interface Props {
  id: string;
  value: string;
  /** The logo saved on the server: once the field equals it, the "press Save" hint is gone. */
  savedValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Message from the last save, shown under the field when it is about the logo. */
  error?: string | null;
  onClearError?: () => void;
}

/** POS-I219: logo link field with an Upload button (PNG or JPEG, checked on the server). */
export function LogoUploadField({ id, value, savedValue, onChange, placeholder, error, onClearError }: Props) {
  const { t } = useTranslation('settings');
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [justUploaded, setJustUploaded] = useState(false);
  const uploadedPath = value.startsWith('/api/v1/public/logos/');
  const shownError = uploadError ?? error ?? null;

  const upload = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    onClearError?.();
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<UploadedLogo>('/tenant/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNotes(res.data.notes ?? []);
      setJustUploaded(true);
      onChange(res.data.url);
    } catch (err) {
      setUploadError(getApiErrorMessage(err, t('logoUploadFailed')));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          aria-label={t('logoUploadButton')}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
        />
        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 className="me-1.5 h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="me-1.5 h-4 w-4" aria-hidden="true" />}
          {uploading ? t('logoUploading') : t('logoUploadButton')}
        </Button>
        <span className="text-xs text-muted-foreground">{t('logoOrPaste')}</span>
      </div>
      {uploadedPath ? (
        <div className="flex items-center gap-2">
          <bdi dir="ltr" className="min-w-0 flex-1 truncate rounded-md border bg-muted/40 px-2 py-1.5 text-xs" title={value}>{value}</bdi>
          <Button type="button" variant="ghost" size="sm" onClick={() => { setNotes([]); setJustUploaded(false); onChange(''); }}>
            {t('logoRemove')}
          </Button>
        </div>
      ) : (
        <Input
          id={id}
          type="text"
          dir="ltr"
          value={value}
          onChange={(e) => { setUploadError(null); setNotes([]); setJustUploaded(false); onClearError?.(); onChange(e.target.value); }}
          placeholder={placeholder}
          aria-invalid={!!shownError}
        />
      )}
      {shownError && <p role="alert" className="text-xs text-destructive-text text-start">{shownError}</p>}
      {value && (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-start gap-3">
            <figure className="m-0 space-y-1">
              <div className="rounded-md border p-2" style={{ backgroundImage: 'repeating-conic-gradient(hsl(var(--muted)) 0% 25%, transparent 0% 50%)', backgroundSize: '12px 12px' }}>
                <img src={value} alt={t('logoPreviewReal')} className="max-h-24 max-w-[200px] object-contain" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
              </div>
              <figcaption className="text-xs text-muted-foreground">{t('logoPreviewReal')}</figcaption>
            </figure>
            <figure className="m-0 space-y-1">
              <div className="rounded-md border bg-white p-2">
                <img src={value} alt={t('logoThermalPreview')} className="max-h-20 max-w-[200px] object-contain" style={{ filter: 'grayscale(1) contrast(1.4)' }} onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
              </div>
              <figcaption className="text-xs text-muted-foreground">{t('logoThermalPreview')}</figcaption>
            </figure>
          </div>
          {justUploaded && value !== savedValue && <p className="text-xs text-muted-foreground">{t('logoUploadedHint')}</p>}
          {notes.map((n) => <p key={n} className="text-xs text-muted-foreground">{n}</p>)}
        </div>
      )}
      <p className="text-xs text-muted-foreground">{t('logoRequirements')}</p>
    </div>
  );
}
